import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@lib/api';
import { FeatureFlagsManager } from '@components/FeatureFlagsManager';

const InstallDetail: React.FC = () => {
  const { installId } = useParams<{ installId: string }>();
  const navigate = useNavigate();
  const [showFlagManager, setShowFlagManager] = useState(false);

  const { data: installData, isLoading, refetch } = useQuery({
    queryKey: ['install-detail', installId],
    queryFn: async () => {
      const response = await apiClient.get<any>(`/api/installs?id=${installId}`);
      return response;
    },
    enabled: !!installId,
  });

  const { data: flagsData, refetch: refetchFlags } = useQuery({
    queryKey: ['install-flags', installId],
    queryFn: async () => {
      const response = await apiClient.get<any>(`/api/install-flags?install_id=${installId}`);
      return response;
    },
    enabled: !!installId,
  });


  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading install details...</div>
      </div>
    );
  }

  const install = installData?.install;
  const license = installData?.license;
  const flags = Array.isArray(flagsData) ? flagsData : [];

  return (
    <div>
      <button
        onClick={() => navigate(-1)}
        className="mb-6 text-blue-600 hover:text-blue-800 underline text-sm"
      >
        ← Back
      </button>

      <div className="bg-white rounded-lg p-6 border border-gray-200 mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">{install?.companyName || 'Install Details'}</h1>

        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-3">
            <div>
              <span className="text-sm text-gray-600 block">Install ID</span>
              <code className="text-lg font-mono bg-gray-100 px-2 py-1 rounded">{install?.installId}</code>
            </div>
            <div>
              <span className="text-sm text-gray-600 block">Company</span>
              <div className="text-lg font-semibold">{install?.companyName}</div>
            </div>
            <div>
              <span className="text-sm text-gray-600 block">Admin Email</span>
              <div className="text-lg">{install?.adminEmail}</div>
            </div>
            <div>
              <span className="text-sm text-gray-600 block">Application</span>
              <div className="text-lg font-semibold">{install?.appId}</div>
            </div>
          </div>

          <div className="space-y-3">
            {install?.sfOrgId && (
              <div>
                <span className="text-sm text-gray-600 block">Salesforce Org ID</span>
                <code className="text-lg font-mono bg-gray-100 px-2 py-1 rounded">{install.sfOrgId}</code>
              </div>
            )}
            {install?.sfOrgType && (
              <div>
                <span className="text-sm text-gray-600 block">Org Type</span>
                <div className="text-lg">{install.sfOrgType}</div>
              </div>
            )}
            {license && (
              <>
                <div>
                  <span className="text-sm text-gray-600 block">License Tier</span>
                  <div className="text-lg font-semibold">{license.tier}</div>
                </div>
                <div>
                  <span className="text-sm text-gray-600 block">License State</span>
                  <span className={`inline-block px-2 py-1 rounded text-sm font-medium ${license.state === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                    {license.state}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <FeatureFlagsManager
        installId={installId || ''}
        flags={flags}
        onFlagsChange={() => refetchFlags()}
        showManage={showFlagManager}
        onManageToggle={setShowFlagManager}
        appId={install?.appId}
      />
    </div>
  );
};

export default InstallDetail;
